import { useEffect, useState } from "react";
import { Loader2, Pencil, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { ScopeBadge } from "@/components/common";

export function CodeBadge({ r, testid }) {
  const st = r?.code_correction?.status;
  if (!st || st === "sap_updated") return null;
  const txt = st === "sap_changed" ? `SAP changed to ${r.code_correction.sap_latest || "—"}: review` : "Differs from SAP: update SAP";
  return <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" title={`SAP code ${r.sap_commodity_code || "—"}`} data-testid={testid}>{txt}</span>;
}

function Preview({ p, err }) {
  if (err) return <p className="text-sm text-rose-600" data-testid="code-preview-error">{err}</p>;
  if (!p) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="code-preview">
      <div className="flex items-center gap-2"><ScopeBadge scope={p.scope_auto} from={p.applies_from} testid="code-preview-scope" /><span className="font-mono">{p.annex_code || "No Annex I row"}</span>{p.commodity && <span>· {p.commodity}</span>}</div>
      {p.annex_condition && <p className="mt-1 text-slate-600" data-testid="code-preview-condition">{p.annex_condition}</p>}
      {p.annex_description && <p className="mt-1 text-slate-500">{p.annex_description}</p>}
    </div>
  );
}

export function CodeDialog({ rec, onClose, onDone }) {
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCode(rec?.commodity_code || ""); setReason(""); }, [rec]);
  useEffect(() => {
    if (!rec || !code) return setP(null);
    const t = setTimeout(() => api.get("/materials/code-preview", { params: { code } }).then((r) => { setP(r.data); setErr(""); }).catch((e) => { setP(null); setErr(errMsg(e)); }), 300);
    return () => clearTimeout(t);
  }, [code, rec]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/materials/by-number/${encodeURIComponent(rec.material)}/code`, { code, reason });
      toast.success(`Code ${p.code} applied to ${r.data.records} plant record(s)${r.data.overrides_reset ? ` · ${r.data.overrides_reset} decision(s) reset` : ""}`);
      onDone();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="code-dialog">
        <DialogHeader><DialogTitle>Correct commodity code · {rec?.material}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Applies to every plant of this material. SAP code: <span className="font-mono" data-testid="code-dialog-sap">{rec?.sap_commodity_code ?? rec?.commodity_code ?? "—"}</span>. An SAP upload won't overwrite it.</p>
        <Input data-testid="code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 4819100000" className="font-mono" />
        <Preview p={p} err={err} />
        <Textarea data-testid="code-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional), e.g. Supplier spec: corrugated cartons" rows={2} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="code-save-btn" onClick={save} disabled={busy || !p}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save code</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditCodeButton({ onClick, testid }) {
  return <Button size="sm" variant="outline" onClick={onClick} data-testid={testid}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit code</Button>;
}

const STATUS = { valid: "text-emerald-700", invalid: "text-rose-600", unknown: "text-amber-700" };

export function CodeBulkDialog({ open, onClose, onDone }) {
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setPv(null); }, [open]);
  const pick = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { setPv((await api.post("/materials/code-corrections/preview", fd)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/materials/code-corrections/apply", { rows: pv.rows.filter((x) => x.status === "valid"), confirm: true });
      toast.success(`${r.data.applied} corrections applied · ${pv.invalid + pv.unknown} rejected`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="code-bulk-dialog">
        <DialogHeader><DialogTitle>Upload code corrections</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Excel or CSV with columns <b>Material</b>, <b>Correct code</b> and optional <b>Reason</b>. Each row applies to every plant of the material.</p>
        <Input type="file" accept=".xlsx,.xls,.csv" data-testid="code-bulk-file" onChange={(e) => pick(e.target.files?.[0])} />
        {pv && (
          <>
            <p className="text-sm" data-testid="code-bulk-summary">{pv.valid} valid · {pv.invalid} invalid · {pv.unknown} unknown material · {pv.scope_changes} change scope</p>
            <div className="scroll-thin max-h-72 overflow-auto rounded border border-slate-200 text-xs">
              <table className="w-full"><tbody className="divide-y divide-slate-100">
                {pv.rows.map((r) => (
                  <tr key={r.row} data-testid={`code-bulk-row-${r.row}`}>
                    <td className="px-2 py-1 text-slate-400">{r.row}</td><td className="px-2 py-1 font-mono">{r.material}</td><td className="px-2 py-1 font-mono">{r.current_code || ""} → {r.code}</td>
                    <td className={`px-2 py-1 font-semibold ${STATUS[r.status]}`}>{r.status}</td>
                    <td className="px-2 py-1">{r.error || (r.scope_changes ? `${r.scope_before} → ${r.scope_after}` : "scope unchanged")}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="code-bulk-apply-btn" onClick={apply} disabled={busy || !pv?.valid}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Apply {pv?.valid || 0} corrections</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
